<template>
  <!-- On a phone the panel bleeds to the viewport edges, per the mobile designs (#6331): the
       negative margins undo the page container's padding around the centered tool. -->
  <aside
    ref="root"
    class="relative flex h-full min-w-0 flex-col justify-between gap-15 overflow-hidden rounded-xl bg-blue p-6 max-lg:mx-[calc(50%-50vw)] max-lg:rounded-none sm:p-10"
    aria-label="Uitleg van de energie-expert"
  >
    <!-- Decoration from the design: soft circles bleeding out of the corners. -->
    <img :src="decoCircleTop" alt="" class="pointer-events-none absolute top-0 left-0" />
    <img :src="decoCircleBottom" alt="" class="pointer-events-none absolute right-0 bottom-20" />

    <div v-if="step !== 'result'" class="relative flex flex-col gap-8">
      <div class="flex flex-col items-center gap-2 pt-8">
        <div class="relative">
          <!-- The origin sits on the centre of the photo, so the rays turn around the sun itself. -->
          <img
            :src="sunRays"
            alt=""
            class="absolute -top-10 left-[50px] w-[131px] origin-[15%_87%] motion-safe:animate-sun-rays"
          />
          <!-- The name and role directly below already tell who this is. -->
          <img :src="michelle" alt="" class="relative size-[138px] rounded-full object-cover ring-4 ring-white" />
        </div>
        <p class="pt-2 text-[32px] font-medium text-white">Michelle</p>
        <p class="text-base font-light text-white">Energie-expert bij Pure Energie</p>
      </div>

      <!-- The card is 423px wide and centered in the design, with the blue panel around it. -->
      <div class="relative mx-auto w-full max-w-[423px]">
        <!-- The speech tail: 27x14 in the design, poking 10px out of the card, 4px overlap against
             hairline gaps. -->
        <div
          class="pointer-events-none absolute -top-[10px] left-1/2 h-[14px] w-[27px] -translate-x-1/2 [clip-path:polygon(50%_0,0_100%,100%_100%)] bg-white"
          aria-hidden="true"
        />
        <div class="rounded-xl bg-white px-6 pt-6 pb-8">
          <!-- Stand-in in panel style: the design for this error state is still open on #6313. -->
          <div v-if="failed" class="flex flex-col gap-4">
            <h3 class="text-[20px] font-bold">{{ failure.title }}</h3>
            <p class="text-base font-light leading-5 text-body">{{ failure.body }}</p>
            <Button @click="emit('retry')">Opnieuw proberen</Button>
          </div>
          <ExpertIntroCard v-else-if="step === 'input'" />
          <ExpertChecklist v-else />
        </div>
      </div>
    </div>

    <div v-else class="relative flex flex-col gap-3">
      <div class="result-item flex flex-col gap-3">
        <!-- Takes the focus the moment the result lands: the button that had it is gone with the
             input step, and without a new anchor the next tab jumps past the panel to the footer.
             tabindex -1 keeps it out of the tab order itself. -->
        <h3 ref="resultHeading" tabindex="-1" class="text-[32px] leading-10 font-medium text-white">
          {{ calculation.paysLess ? 'Je gaat minder betalen' : 'Ik heb de impact voor je berekend.' }}
        </h3>
        <p class="text-[18px] font-light text-white">
          {{
            calculation.paysLess
              ? 'Vanaf 2027 betaal je naar verwachting minder voor stroom.'
              : 'Vanaf 1 januari 2027 gaat je energierekening omhoog.'
          }}
        </p>
      </div>
      <ResultSummary @restart="emit('restart')" />
    </div>

    <ExpertProgress
      v-if="progress"
      class="relative mx-auto w-full max-w-[423px]"
      :label="progress.label"
      :percentage="progress.percentage"
      :transition-ms="progress.transitionMs"
      :busy="progress.busy"
    />

    <!-- Tells screen readers where the calculation stands; the checklist itself is decor. -->
    <p class="sr-only" role="status">{{ announcement }}</p>
  </aside>
</template>

<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue'

import decoCircleBottom from '@/assets/svg/deco-circle-bottom.svg'
import decoCircleTop from '@/assets/svg/deco-circle-top.svg'
import sunRays from '@/assets/svg/sun-rays.svg'
import michelle from '@/assets/images/michelle.png'
import Button from '@/components/Button.vue'
import ExpertChecklist from '@/components/expert-panel/ExpertChecklist.vue'
import ExpertIntroCard from '@/components/expert-panel/ExpertIntroCard.vue'
import ExpertProgress from '@/components/expert-panel/ExpertProgress.vue'
import ResultSummary from '@/components/expert-panel/ResultSummary.vue'
import { CALCULATION_STEPS, START_PERCENTAGE, useAnimationStore } from '@/stores/animation'
import { useCalculationStore } from '@/stores/calculation'
import type { Step } from '@/types/flow'
import { formatEuro } from '@/utils/currency'

const props = defineProps<{
  step: Step
}>()

const emit = defineEmits<{ retry: []; restart: [] }>()

const animation = useAnimationStore()
const calculation = useCalculationStore()

const resultHeading = useTemplateRef<HTMLElement>('resultHeading')

// The comment above the aside makes this a fragment, so $el is that comment node and not the
// panel; App.vue scrolls to the panel and needs the element itself.
const root = useTemplateRef<HTMLElement>('root')

defineExpose({ root })

// Keyboard order after the calculation: on to the result, not out of the tool.
watch(
  () => props.step,
  async (step) => {
    if (step !== 'result') {
      return
    }

    await nextTick()
    resultHeading.value?.focus()
  },
)

// The request never made it, or the limiter stopped it. That happens before the animation starts,
// so the panel shows it on the input step as well, where the form still holds the input the retry
// uses.
const failed = computed(() => calculation.status === 'failed' || calculation.status === 'throttled')

/**
 * Two different dead ends, two messages: something broke and a retry may well work, against the
 * limiter, where only waiting helps and "probeer het direct opnieuw" would be wrong. capi words the
 * limiter case; without its message there is nothing to promise, so the general one stands.
 */
const failure = computed(() => {
  if (calculation.status === 'throttled' && calculation.throttleNotice) {
    return { title: 'Even wachten met rekenen', body: calculation.throttleNotice }
  }

  return {
    title: 'De berekening is niet gelukt',
    body:
      'Er ging iets mis bij het maken van jouw berekening. Je invoer is bewaard, dus je kunt het direct opnieuw ' +
      'proberen.',
  }
})

// While calculating the bar follows the animation. The result screen has no bar (the flow is
// complete there) and a failure hides it: a frozen percentage next to the message reads as stuck.
const progress = computed(() => {
  if (failed.value) {
    return undefined
  }

  if (props.step === 'input') {
    return { label: 'Persoonlijke berekening starten', percentage: START_PERCENTAGE, transitionMs: 0, busy: false }
  }

  if (props.step === 'calculating') {
    return {
      label: 'Ik maak jouw persoonlijke berekening ...',
      percentage: animation.percentage,
      transitionMs: animation.barDuration,
      busy: true,
    }
  }

  return undefined
})

const announcement = computed(() => {
  if (failed.value) {
    return failure.value.title
  }

  if (props.step === 'calculating') {
    // Past the last step (settling or finished) the label runs out; announce the near end.
    return CALCULATION_STEPS[animation.completedSteps]?.label ?? 'De berekening is bijna klaar.'
  }

  return props.step === 'result' ? resultAnnouncement.value : ''
})

// The outcome itself, not just "done": a screen reader hears the amounts the sighted visitor sees
// land in the orange block.
const resultAnnouncement = computed(() => {
  const result = calculation.result

  if (calculation.status === 'calculated' && result?.difference) {
    const { monthly, annual } = result.difference

    // Spoken out loud a minus sign in front of the amount says nothing, so the direction sits in
    // the words and the amounts themselves are read without it.
    if (calculation.paysLess) {
      return (
        `De berekening is klaar. Vanaf 2027 betaal je naar verwachting ${formatEuro(Math.abs(monthly))} ` +
        `per maand minder, ongeveer ${formatEuro(Math.abs(annual))} per jaar.`
      )
    }

    return (
      `De berekening is klaar. Vanaf 2027 betaal je naar verwachting ${formatEuro(monthly)} ` +
      `per maand extra, ongeveer ${formatEuro(annual)} per jaar.`
    )
  }

  return 'De berekening is klaar. We kunnen dit nu niet berekenen.'
})
</script>
